import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal } from "antd";
import { CheckOutlined } from "@ant-design/icons";

// Import Vector Icons from the local icons folder
import AssignedIcon from "./icons/Vector.svg";
import ManageIcon from "./icons/Vector-1.svg";
import FootprintIcon from "./icons/Vector-2.svg";
import OffsetGoLogo from "./icons/OffsetGo.svg";
import BannerGraphic from "./icons/image 1592.svg";
import RefurbLogo from "./icons/image 1591.svg";
import Tip1Icon from "./icons/Vector-3.svg";
import Tip2Icon from "./icons/Vector-4.svg";
import Tip3Icon from "./icons/Vector-5.svg";
import { ReactComponent as EmployeeIcon } from "../../../../assets/svgs/Employee.svg";
import { ReactComponent as UserIcon } from "../../../../assets/svgs/User.svg";
import { ReactComponent as VendorIcon } from "../../../../assets/svgs/Vendor.svg";

function Home() {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const [justLoggedIn] = useState(!!location.state?.showWelcomeModal);

  useEffect(() => {
    if (location.state?.showWelcomeModal) {
      Modal.success({
        icon: null,
        centered: true,
        closable: false,
        className: "custom-success-modal",
        okButtonProps: { style: { display: "none" } },
        content: (
          <div className="flex flex-col items-center text-center -mt-6 -mx-6 -mb-6">
            {/* Green Header */}
            <div className="w-full bg-[#06513D] px-6 py-3 text-white text-center font-semibold text-lg">
              Welcome To Carburn!
            </div>

            {/* Modal Body */}
            <div className="w-full bg-white  py-4">
              <div className="bg-[#06513D] p-3 rounded-full mx-auto w-fit mb-6">
                <CheckOutlined style={{ fontSize: "40px", color: "white" }} />
              </div>
              <p className="text-[#06513D] text-base mb-6 font-medium">
                Empowering your Net Zero journey
              </p>
            </div>
          </div>
        ),
      });

      const timer = setTimeout(() => {
        Modal.destroyAll();
      }, 2000);

      // Clear the router state so the modal doesn't show again on manual page actions/refresh
      window.history.replaceState({}, document.title);

      return () => {
        clearTimeout(timer);
        Modal.destroyAll();
      };
    }
  }, [location.state]);

  const quickActions = [
    {
      title: "Assigned Forms",
      description: "View and fill assigned forms.",
      buttonText: "Open Forms",
      route: "/employee/form-assigned",
      isExternal: false,
      icon: (
        <div className="bg-[#06513D] rounded-lg p-3.5 w-14 h-14 flex justify-center items-center shadow-sm border border-emerald-800">
          <img
            src={AssignedIcon}
            className="w-7 h-7 object-contain"
            alt="Assigned Forms"
          />
        </div>
      ),
    },
    {
      title: "Manage Forms",
      description: "Manage delegated forms and track progress.",
      buttonText: "Manage Now",
      route: "/employee/manage-form",
      isExternal: false,
      icon: (
        <div className="bg-[#06513D] rounded-lg p-3.5 w-14 h-14 flex justify-center items-center shadow-sm border border-emerald-800">
          <img
            src={ManageIcon}
            className="w-7 h-7 object-contain"
            alt="Manage Forms"
          />
        </div>
      ),
    },
    {
      title: "Individual Footprint",
      description: "Calculate your personal carbon footprint.",
      buttonText: "Start Calculation",
      route: "https://mycarbonfootprint.offsetgo.earth/",
      isExternal: true,
      icon: (
        <div className="bg-[#06513D] rounded-lg p-3.5 w-14 h-14 flex justify-center items-center shadow-sm border border-emerald-800">
          <img
            src={FootprintIcon}
            className="w-7 h-7 object-contain"
            alt="Individual Footprint"
          />
        </div>
      ),
    },
    {
      title: "OffsetGo Marketplace",
      description: "Explore sustainability services and solutions.",
      buttonText: "Visit Marketplace",
      route: "https://marketplace.offsetgo.earth/",
      isExternal: true,
      icon: (
        <div className="bg-[#06513D] rounded-lg p-1.5 w-14 h-14 flex justify-center items-center shadow-sm border border-gray-200">
          <img
            src={OffsetGoLogo}
            className="w-full h-full object-contain"
            alt="OffsetGo Logo"
          />
        </div>
      ),
    },
    {
      title: "RefurbBazaar",
      description: "Buy sustainable refurbished IT products.",
      buttonText: "Visit Store",
      route: "https://refurbbazaar.com",
      isExternal: true,
      icon: (
        <div className="bg-[#06513D] rounded-lg p-1.5 w-14 h-14 flex justify-center items-center shadow-sm border border-gray-200">
          <img
            src={RefurbLogo}
            className="w-full h-full object-contain"
            alt="RefurbBazaar Logo"
          />
        </div>
      ),
    },
  ];

  const sustainabilityTips = [
    {
      text: "Switch off unused devices to reduce energy consumption.",
      icon: (
        <div className="bg-[#06513D] rounded-xl w-12 h-12 flex-shrink-0 flex items-center justify-center shadow-sm border border-emerald-950">
          <img
            src={Tip1Icon}
            className="w-6 h-6 object-contain"
            alt="Energy Tip"
          />
        </div>
      ),
    },
    {
      text: "Use virtual meetings to reduce travel emissions.",
      icon: (
        <div className="bg-[#06513D] rounded-xl w-12 h-12 flex-shrink-0 flex items-center justify-center shadow-sm border border-emerald-950">
          <img
            src={Tip2Icon}
            className="w-6 h-6 object-contain"
            alt="Virtual Meeting Tip"
          />
        </div>
      ),
    },
    {
      text: "Choose refurbished electronics to support circular economy.",
      icon: (
        <div className="bg-[#06513D] rounded-xl w-12 h-12 flex-shrink-0 flex items-center justify-center shadow-sm border border-emerald-950">
          <img
            src={Tip3Icon}
            className="w-6 h-6 object-contain"
            alt="Circular Economy Tip"
          />
        </div>
      ),
    },
  ];

  const handleActionClick = (action) => {
    if (action.isExternal) {
      window.open(action.route, "_blank");
    } else {
      navigate(action.route);
    }
  };

  return (
    <div className="py-6 px-3 sm:px-4 md:px-6 lg:px-0 font-sans">
      <div className="w-full space-y-8">
        {/* Welcome Banner */}
        <div className="relative overflow-hidden bg-[#E8F0EE] rounded-[20px] border border-[#06513D] sm:p-8 flex items-center min-h-[110px] sm:min-h-[140px] shadow-sm">
          {/* Welcome Text + Roles */}
          <div className="z-10 flex flex-col gap-2">
            <h1 className="text-gray-900 font-medium tracking-tight">
              <span className="text-[18px]">
                {justLoggedIn ? "Welcome," : "Welcome back,"}
              </span>{" "}
              <span className="text-[#06513D] text-[30px] font-bold">
                {user?.name
                  ? user.name.charAt(0).toUpperCase() + user.name.slice(1)
                  : "Rahul"}{" "}
                👋
              </span>
            </h1>

            {/* Active Roles */}
            {user?.role?.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <span className="text-[18px] font-semibold mb-2">
                  Active Roles:
                </span>
                <div className="flex flex-wrap gap-2">
                  {user.role.map((role) => {
                    const roleConfig = {
                      employee: {
                        icon: <EmployeeIcon className="w-3.5 h-3.5" />,
                      },
                      vendor: {
                        icon: <VendorIcon className="w-3.5 h-3.5" />,
                      },
                      user: {
                        icon: <UserIcon className="w-3.5 h-3.5" />,
                      },
                    };
                    const config =
                      roleConfig[role.toLowerCase()] || roleConfig.user;
                    return (
                      <span
                        key={role}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#195D39] bg-white text-[#06513D] text-[12px] font-semibold"
                      >
                        {config.icon}
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Banner Graphic (Right side decoration) */}
          <div className="absolute right-0 top-0 bottom-0 h-full w-[240px] md:w-[360px] overflow-hidden pointer-events-none hidden sm:block">
            <img
              src={BannerGraphic}
              className="w-full h-full object-contain object-right"
              alt="Banner Graphic"
            />
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="space-y-4">
          <h2 className="text-gray-900 text-lg sm:text-xl font-bold tracking-tight px-1">
            Quick Actions
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {quickActions.map((action, idx) => (
              <div
                key={idx}
                onClick={() => handleActionClick(action)}
                className="bg-[#E8F0EE] rounded-[20px] border border-[#06513D] p-5 flex flex-col justify-between h-[250px] hover:border-[#B7E5DC] transition-all duration-300 cursor-pointer group"
                style={{ boxShadow: "5px 6px 8.9px 0px #30B7921F" }}
              >
                {/* Card Top: Icon & Text */}
                <div className="space-y-4">
                  <div>{action.icon}</div>
                  <div className="space-y-1.5">
                    <h3 className="text-gray-900 text-base font-bold leading-tight font-sans">
                      {action.title}
                    </h3>
                    <p className="text-gray-500 text-[12.5px] leading-snug font-medium">
                      {action.description}
                    </p>
                  </div>
                </div>

                {/* Card Bottom: Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActionClick(action);
                  }}
                  className="w-full bg-[#06513D] text-white text-xs sm:text-[13px] font-semibold py-2.5 rounded-lg hover:bg-[#086149] transition-all duration-200 shadow-sm border border-emerald-950 flex items-center justify-center group-hover:bg-[#086149]"
                >
                  {action.buttonText}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Sustainability Tips Section */}
        <div className="space-y-4">
          <h2 className="text-gray-900 text-lg sm:text-xl font-bold tracking-tight px-1">
            Sustainability Tips
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sustainabilityTips.map((tip, idx) => (
              <div
                key={idx}
                className="bg-[#E8F0EE] rounded-[16px] border border-[#06513D] p-4 flex items-center gap-4"
                // style={{ boxShadow: "5px 6px 8.9px 0px #30B7921F" }}
              >
                {/* Tip Icon Box */}
                {tip.icon}
                {/* Tip Text */}
                <p className="text-gray-800 text-[13px] sm:text-sm font-medium leading-snug">
                  {tip.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
